import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/api';
import { RestaurantAttendance, RestaurantPage } from '../types/restaurantPersonnel';
import { extractApiErrorMessage } from '../utils/apiError';
import { buildRestaurantEmployeePrintHtml, PayrollHistoryItem } from '../utils/restaurantEmployeePrint';
import { printHtmlInHiddenFrame } from '../utils/printDocument';
import { attendanceMonthRange } from './RestaurantAttendanceHistory';

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const RestaurantEmployeePrintControl: React.FC<{
  employee: { id: number; fullName: string; position: string };
  canViewSalary: boolean;
  disabled: boolean;
}> = ({ employee, canViewSalary, disabled }) => {
  const [month, setMonth] = useState(currentMonth);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const request = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const cancel = useCallback(() => {
    generation.current++;
    request.current?.abort();
    request.current = null;
  }, []);
  useEffect(() => {
    setWorking(false);
    setError('');
    return cancel;
  }, [employee.id, month, canViewSalary, cancel]);

  const print = async () => {
    if (disabled || request.current) return;
    const controller = new AbortController();
    const printGeneration = ++generation.current;
    request.current = controller;
    const valid = () => !controller.signal.aborted && request.current === controller;
    setWorking(true);
    setError('');
    const loadAttendance = async () => {
      const items: RestaurantAttendance[] = [];
      let total = 0;
      for (let page = 1; ; page++) {
        const response = await api.get<RestaurantPage<RestaurantAttendance>>(
          `/restaurant-personnel/${employee.id}/attendance`,
          { signal: controller.signal, params: { ...attendanceMonthRange(month), page, pageSize: 100 } },
        );
        if (!valid()) return items;
        if (page === 1) total = response.data.total;
        if (response.data.total !== total || (items.length < total && !response.data.items.length))
          throw new Error('Табель змінився під час завантаження. Повторіть друк.');
        items.push(...response.data.items);
        if (items.length >= total) return items;
      }
    };
    const loadSalary = async () => {
      if (!canViewSalary) return { history: null, error: '' };
      try {
        const response = await api.get<PayrollHistoryItem[]>(
          `/restaurant-payroll/employments/${employee.id}/history`, { signal: controller.signal },
        );
        return { history: response.data, error: '' };
      } catch (failure) {
        return { history: null, error: extractApiErrorMessage(failure, 'Не вдалося завантажити історію зарплати ресторану.') };
      }
    };
    try {
      const [attendance, salary, detail] = await Promise.all([
        loadAttendance(),
        loadSalary(),
        api.get<{ id: number; fullName: string; position: string }>(
          `/restaurant-personnel/${employee.id}`, { signal: controller.signal },
        ),
      ]);
      if (!valid()) return;
      if (detail.data.id !== employee.id)
        throw new Error('Отримано дані іншого працівника. Повторіть друк.');
      if (salary.error) setError(salary.error);
      printHtmlInHiddenFrame(buildRestaurantEmployeePrintHtml({
        month, employee: { fullName: detail.data.fullName, position: detail.data.position, attendance }, canViewSalary,
        payrollHistory: salary.history, payrollHistoryLoading: false, payrollHistoryError: salary.error,
      }), { onError: failure => {
        if (generation.current === printGeneration)
          setError(extractApiErrorMessage(failure, 'Не вдалося підготувати бланк до друку.'));
      } });
    } catch (failure) {
      if (valid()) setError(extractApiErrorMessage(failure, 'Не вдалося завантажити дані для друку бланка.'));
    } finally {
      if (request.current === controller) {
        controller.abort();
        request.current = null;
        setWorking(false);
      }
    }
  };

  return <section className="restaurant-personnel__print" aria-label="Друк бланка працівника" aria-busy={working}>
    <div className="restaurant-personnel__actions">
      <label>Місяць бланка<input type="month" required min="2000-01" max="2200-12"
        value={month} disabled={disabled || working} onChange={event => {
          if (/^\d{4}-(0[1-9]|1[0-2])$/.test(event.target.value)) {
            cancel();
            setMonth(event.target.value);
          }
        }} /></label>
      <button type="button" disabled={disabled || working} onClick={() => void print()}>
        Друк бланка працівника / PDF за місяць
      </button>
    </div>
    {working && <p role="status">Підготовка бланка: завантаження всіх інтервалів за місяць…</p>}
    {error && <p role="alert">{error}</p>}
    <p>Табель і деталізація затвердженої зарплати за вибраний місяць.
      {!canViewSalary && ' Зарплатні дані недоступні без окремого дозволу.'}</p>
  </section>;
};

export default RestaurantEmployeePrintControl;
